import { useEffect, useMemo, useRef, useState } from 'react';

import { UploadMediaItem } from '../../../Context/ProgressContext';

export const useUploadPreview = (uploadMediaList: UploadMediaItem[]) => {
    const [uploadPreviewMap, setUploadPreviewMap] = useState<Record<string, string>>({});
    const uploadPreviewMapRef = useRef<Record<string, string>>({});

    const uploadingMediaData = useMemo(
        () =>
            uploadMediaList.map((mediaItem) => ({
                id: mediaItem.id,
                mimeType: mediaItem.file.type,
                previewUrl: uploadPreviewMap[mediaItem.id] || '',
                isUploading: mediaItem.isUploading,
                errorText: mediaItem.errorText || '',
            })),
        [uploadMediaList, uploadPreviewMap]
    );

    useEffect(() => {
        const currentIds = new Set(uploadMediaList.map((mediaItem) => mediaItem.id));
        setUploadPreviewMap((prevState) => {
            const nextState: Record<string, string> = {};
            Object.entries(prevState).forEach(([id, url]) => {
                if (currentIds.has(id)) {
                    nextState[id] = url;
                } else if (url.startsWith('blob:')) {
                    URL.revokeObjectURL(url);
                }
            });
            return nextState;
        });
    }, [uploadMediaList]);

    useEffect(() => {
        uploadPreviewMapRef.current = uploadPreviewMap;
    }, [uploadPreviewMap]);

    useEffect(() => {
        let isCancelled = false;
        const createdBlobUrls: string[] = [];

        const createVideoFallbackPoster = () => {
            const canvas = document.createElement('canvas');
            canvas.width = 320;
            canvas.height = 180;
            const ctx = canvas.getContext('2d');
            if (!ctx) {
                return '';
            }

            ctx.fillStyle = '#eef1f6';
            ctx.fillRect(0, 0, canvas.width, canvas.height);

            ctx.fillStyle = '#2f3a4c';
            ctx.globalAlpha = 0.9;
            ctx.beginPath();
            ctx.moveTo(135, 70);
            ctx.lineTo(135, 110);
            ctx.lineTo(175, 90);
            ctx.closePath();
            ctx.fill();
            ctx.globalAlpha = 1;

            return canvas.toDataURL('image/png');
        };

        const createVideoFramePreview = (file: File) =>
            new Promise<string>((resolve) => {
                const fileObjectUrl = URL.createObjectURL(file);
                const video = document.createElement('video');
                video.preload = 'metadata';
                video.muted = true;
                video.src = fileObjectUrl;

                const resolveFallback = () => {
                    URL.revokeObjectURL(fileObjectUrl);
                    resolve(createVideoFallbackPoster());
                };

                const resolveFrame = () => {
                    try {
                        const canvas = document.createElement('canvas');
                        canvas.width = video.videoWidth || 320;
                        canvas.height = video.videoHeight || 180;
                        const ctx = canvas.getContext('2d');
                        if (!ctx) {
                            resolveFallback();
                            return;
                        }
                        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
                        const sampleWidth = 24;
                        const sampleHeight = 14;
                        const sampleCanvas = document.createElement('canvas');
                        sampleCanvas.width = sampleWidth;
                        sampleCanvas.height = sampleHeight;
                        const sampleCtx = sampleCanvas.getContext('2d');
                        if (sampleCtx) {
                            sampleCtx.drawImage(canvas, 0, 0, sampleWidth, sampleHeight);
                            const imageData = sampleCtx.getImageData(
                                0,
                                0,
                                sampleWidth,
                                sampleHeight
                            ).data;
                            let darkPixels = 0;
                            for (let index = 0; index < imageData.length; index += 4) {
                                const red = imageData[index];
                                const green = imageData[index + 1];
                                const blue = imageData[index + 2];
                                const luma = 0.2126 * red + 0.7152 * green + 0.0722 * blue;
                                if (luma < 35) {
                                    darkPixels += 1;
                                }
                            }
                            const totalPixels = sampleWidth * sampleHeight;
                            if (darkPixels / totalPixels > 0.8) {
                                URL.revokeObjectURL(fileObjectUrl);
                                resolve(createVideoFallbackPoster());
                                return;
                            }
                        }
                        const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
                        URL.revokeObjectURL(fileObjectUrl);
                        resolve(dataUrl);
                    } catch {
                        resolveFallback();
                    }
                };

                const timeoutId = window.setTimeout(resolveFallback, 2000);
                const safeResolveFrame = () => {
                    window.clearTimeout(timeoutId);
                    resolveFrame();
                };
                const safeResolveFallback = () => {
                    window.clearTimeout(timeoutId);
                    resolveFallback();
                };

                video.onloadedmetadata = () => {
                    try {
                        const duration = video.duration || 0;
                        video.currentTime = Math.min(1, Math.max(0.2, duration * 0.25));
                    } catch {
                        safeResolveFallback();
                    }
                };
                video.onseeked = safeResolveFrame;
                video.onerror = safeResolveFallback;
            });

        const buildMissingPreviews = async () => {
            const missingMediaItems = uploadMediaList.filter(
                (mediaItem) => !uploadPreviewMap[mediaItem.id]
            );

            if (!missingMediaItems.length) {
                return;
            }

            const previewPromises = missingMediaItems.map(async (mediaItem) => {
                const { file } = mediaItem;
                const fileName = file.name.toLowerCase();
                const isVideoByExtension =
                    fileName.endsWith('.mp4') ||
                    fileName.endsWith('.mov') ||
                    fileName.endsWith('.avi') ||
                    fileName.endsWith('.m4v') ||
                    fileName.endsWith('.wmv') ||
                    fileName.endsWith('.webm') ||
                    fileName.endsWith('.asf') ||
                    fileName.endsWith('.mkv');
                const isVideoFile = file.type.startsWith('video/') || isVideoByExtension;

                const preview = isVideoFile
                    ? await createVideoFramePreview(file)
                    : URL.createObjectURL(file);

                if (preview.startsWith('blob:')) {
                    createdBlobUrls.push(preview);
                }

                return [mediaItem.id, preview] as [string, string];
            });

            const nextEntries = await Promise.all(previewPromises);

            if (isCancelled) {
                createdBlobUrls.forEach((url) => URL.revokeObjectURL(url));
                return;
            }

            setUploadPreviewMap((prevState) => ({
                ...prevState,
                ...Object.fromEntries(nextEntries),
            }));
        };

        void buildMissingPreviews();

        return () => {
            isCancelled = true;
        };
    }, [uploadMediaList, uploadPreviewMap]);

    useEffect(
        () => () => {
            Object.values(uploadPreviewMapRef.current).forEach((url) => {
                if (url.startsWith('blob:')) {
                    URL.revokeObjectURL(url);
                }
            });
        },
        []
    );

    return { uploadingMediaData };
};
